import React, { useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { I18nProvider as AriaI18nProvider, useLocale } from '@react-aria/i18n';
import {
  DateFormatLocaleContext,
  DateFormatLocaleContextValue,
  ThemeContext,
  ThemeContextProps,
} from '@danske/sapphire-react-context';

import type { Theme } from '@danske/sapphire-css/themes/index';
import { BreakpointProvider } from '../../BreakpointProvider';
import { ThemeRoot } from '../../ThemeRoot';
import { SapphireViewStyleProps } from '../../View';
import { useBreakpointTokens } from '../../utils/useBreakpointTokens';
import { UNSAFE_PortalProvider } from '@react-aria/overlays';

export type ThemeVariant = 'primary' | 'secondary' | 'tertiary' | 'contrast';

export interface SapphireProviderProps
  extends Omit<SapphireViewStyleProps, 'backgroundColor' | 'color'> {
  locale?:
    | 'en-US'
    | 'da-DK'
    | 'en-DK'
    | 'nb-NO'
    | 'en-NO'
    | 'sv-SE'
    | 'en-SE'
    | 'fi-FI'
    | 'en-FI'
    | 'sv-FI'
    | 'pl-PL'
    | 'en-PL'
    | 'de-DE'
    | 'en-DE'
    //The added '& NonNullable<unknown>' lets us have autocomplete/suggestions for the above values but leaves the type with a string fallback type.
    | (string & NonNullable<unknown>);
  /**
   * The value provided here is an import of a theme from @danske/sapphire-react.
   */
  theme: Theme;
  /**
   * The theme variation, in case the theme supports one
   *
   * @default 'primary'
   */
  themeVariant?: ThemeVariant;
  /**
   * Option for setting base z-index for Sapphire components. Use this 'overlayZIndex' to calculate
   * new z-index values of components using this new set baseline.
   * @default 100
   */
  overlayZIndex?: number;
  /**
   * A custom locale to be used only for date formatting in those components
   * that show dates.
   * You most likely do NOT need this. Use only if you explicitly want to
   * customise date formatting to be different than the language.
   */
  customDateFormatLocale?: string | null;
  children: React.ReactNode;
}

export const useThemeContext = (): ThemeContextProps<Theme, ThemeVariant> =>
  useContext(ThemeContext) as ThemeContextProps<Theme, ThemeVariant>;

export const useCustomDateFormatLocale = (): DateFormatLocaleContextValue =>
  useContext(DateFormatLocaleContext);

export const SapphireProvider = (props: SapphireProviderProps): JSX.Element => {
  const {
    overlayZIndex = 100,
    children,
    customDateFormatLocale,
    locale,
    theme,
    themeVariant,
    ...otherProps
  } = props;
  const wrapper = useRef<HTMLDivElement>(null);

  const breakpoints = useBreakpointTokens({ ref: wrapper });

  if (theme === undefined) {
    throw new Error(
      'Value of "theme" in SapphireProvider is "undefined". Make sure to pass a theme to SapphireProvider. If you are passing a theme but its value is undefined, it may be caused by your build tools configuration not supporting css modules.'
    );
  }

  let currentLocale = useLocale().locale;
  if (!locale) {
    // react-aria's useLocale can default to a locale unsupported by us if it
    // was not provided we want en-DK to be default
    currentLocale = 'en-DK';
  }

  const currentCustomDateFormatLocale = useCustomDateFormatLocale();

  const overlayContainerRef = useRef<HTMLDivElement>(null);

  return (
    <ThemeContext.Provider value={{ overlayZIndex, theme, themeVariant }}>
      <BreakpointProvider breakpoints={breakpoints}>
        <AriaI18nProvider locale={locale || currentLocale}>
          <DateFormatLocaleContext.Provider
            value={
              typeof customDateFormatLocale === 'undefined'
                ? currentCustomDateFormatLocale
                : customDateFormatLocale
            }
          >
            <UNSAFE_PortalProvider
              getContainer={() => {
                if (overlayContainerRef.current) {
                  overlayContainerRef.current.removeAttribute('inert');
                }
                return overlayContainerRef.current;
              }}
            >
              <ThemeRoot {...otherProps} ref={wrapper}>
                {children}
                {typeof document !== 'undefined' &&
                  ReactDOM.createPortal(
                    <ThemeRoot
                      variant="primary"
                      noSurface
                      ref={overlayContainerRef}
                      UNSAFE_style={{ zIndex: overlayZIndex }}
                    >
                      {null}
                    </ThemeRoot>,
                    document.body
                  )}
              </ThemeRoot>
            </UNSAFE_PortalProvider>
          </DateFormatLocaleContext.Provider>
        </AriaI18nProvider>
      </BreakpointProvider>
    </ThemeContext.Provider>
  );
};
